<template>
  <div class="right-header">
    <div class="branch" v-if="!!branch">{{ branch }}</div>
    <div class="split-v" />

    <div class="notifications">
      <div class="icon-wrapper" v-if="false">
        <font-awesome-icon class="icon" :icon="faBell" />
        <div class="indicator" />
      </div>

      <div class="icon-wrapper" :class="{ notify: notifyingCallLog }">
        <toggle-box v-if="!(isMobile || isTablet)" ref="callLogToggle" hide-indicator>
          <template v-slot:control>
            <font-awesome-icon @click="toggleCallLog" class="icon" :icon="faPhone" />
            <div class="indicator" />
          </template>
          <template v-slot:content>
            <ul class="call-log">
              <li
                v-for="call in callLogs"
                :key="call.id"
              >
                <font-awesome-icon
                  :icon="faArrowUp"
                  class="icon"
                  :class="{
                    inbound: (call.direction === CALL_DIRECTIONS.INBOUND),
                    green: (call.status === CALL_LOG_STATUSES.ANSWERED),
                    red: (call.status === CALL_LOG_STATUSES.MISSED)
                  }"
                />
                <div class="info">
                  <div class="number">
                    <h5 @click="startCall(call.remote)">{{ call.remote }}</h5>
                    <p v-if="call.client">{{ `${call.client.firstName} ${call.client.lastName}` }}</p>
                  </div>
                  <p class="grey">
                    <small>{{ moment(call.duration, DURATION_FORMAT).format('mm:ss') }}</small>
                    <small> | </small>
                    <small>{{ moment(call.createdAt).format('DD-MM-YYYY HH:mm') }}</small>
                    <br />
                    <small v-if="!((call.status === CALL_LOG_STATUSES.MISSED) && (call.direction === CALL_DIRECTIONS.INBOUND))">{{ `${call.agent.firstName} ${call.agent.lastName}` }}</small>
                  </p>
                </div>
              </li>
            </ul>
          </template>
        </toggle-box>
        <div v-else>
          <font-awesome-icon @click="toggleCallLog" class="icon" :icon="faPhone" />
          <div class="indicator" />
          <bottom-sheet v-model="callBottomSheetOpen">
            <ul class="call-log">
              <li
                v-for="call in callLogs"
                :key="call.id"
              >
                <font-awesome-icon
                  :icon="faArrowUp"
                  class="icon"
                  :class="{
                    inbound: (call.direction === CALL_DIRECTIONS.INBOUND),
                    green: (call.status === CALL_LOG_STATUSES.ANSWERED),
                    red: (call.status === CALL_LOG_STATUSES.MISSED)
                  }"
                />
                <div class="info">
                  <div class="number">
                    <h5 @click="startCall(call.remote)">{{ call.remote }}</h5>
                    <p v-if="call.client">{{ `${call.client.firstName} ${call.client.lastName}` }}</p>
                  </div>
                  <p class="grey">
                    <small>{{ moment(call.duration, DURATION_FORMAT).format('mm:ss') }}</small>
                    <small> | </small>
                    <small>{{ moment(call.createdAt).format('DD-MM-YYYY HH:mm') }}</small>
                    <br />
                    <small v-if="!((call.status === CALL_LOG_STATUSES.MISSED) && (call.direction === CALL_DIRECTIONS.INBOUND))">{{ `${call.agent.firstName} ${call.agent.lastName}` }}</small>
                  </p>
                </div>
              </li>
            </ul>
          </bottom-sheet>
        </div>
      </div>
    </div>
    <div class="split-v" />

    <dial-pad @click="toggleDialPad" />
    <div class="profile" @click="() => toggleProfileMenu()">
      <toggle-box ref="profileMenu" hide-indicator>
        <template v-slot:control>
          <div class="name">{{ fullName }}</div>
          <div class="pic">
            <img :src="profile.profilePicture || DEFAULT_PROFILE_PICTURE" :alt="fullName" />
            <div class="phone-connectivity" :class="{ connected }" />
          </div>
        </template>
        <template v-slot:content>
          <div class="profile-menu">
            <div class="controls">
              <div class="connection-status" :class="{ disconnected: !connected }">
                <div class="bullet" />
                <span>{{ $t(connected ? 'CONNECTED' : 'DISCONNECTED') }}</span>
              </div>
            </div>
            <div class="split" />
            <ul>
              <li @click="changePassword">{{ $t('CHANGE_PASSWORD') }}</li>
              <li @click="logout">{{ $t('LOG_OUT') }}</li>
            </ul>
          </div>
        </template>
      </toggle-box>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { faSearch, faPhone, faBell, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import DialPad from '@/icons/DialPad';
import ToggleBox from '@/components/ToggleBox';
import { DEFAULT_PROFILE_PICTURE, CALL_DIRECTIONS, CALL_LOG_STATUSES, DURATION_FORMAT, PATHS } from '@/consts';
import BottomSheet from '@/components/BottomSheet';

const { useState: usePhoneState, useActions: usePhoneActions } = createNamespacedHelpers('phone');
const { useState: useUserState, useActions: useUserActions } = createNamespacedHelpers('user');
const { useState: useAppState } = createNamespacedHelpers('app');

export default {
  name: 'Right-Header',
  components: {
    FontAwesomeIcon,
    DialPad,
    ToggleBox,
    BottomSheet,
  },
  setup(props, { root: { $router } }) {
    const { profile, branch } = useUserState(['profile', 'branch']);
    const { connected, callLogs, notifyingCallLog } = usePhoneState(['connected', 'callLogs', 'notifyingCallLog']);
    const { isMobile, isTablet } = useAppState(['isMobile', 'isTablet']);
    const { startOutboundCall, unnotifyCallLog, toggleDialPad, getCallLogs } = usePhoneActions(['startOutboundCall', 'unnotifyCallLog', 'toggleDialPad', 'getCallLogs']);
    const { logout } = useUserActions(['logout']);

    const fullName = computed(() => `${profile.value.firstName} ${profile.value.lastName}`);

    const callLogToggle = ref(null);
    const callBottomSheetOpen = ref(false);
    const profileMenu = ref(null);

    const toggleProfileMenu = () => {
      if (!profileMenu.value) return;
      profileMenu.value.toggle();
    };

    const toggleCallLog = () => {
      if (isMobile.value || isTablet.value) {
        callBottomSheetOpen.value = !callBottomSheetOpen.value;
      } else {
        if (!callLogToggle.value) return;
        callLogToggle.value.toggle();
      }
      unnotifyCallLog();
    };

    const startCall = (number) => {
      startOutboundCall(number);
    };

    const changePassword = () => {
      $router.push(PATHS.USER_PASSWORD);
    };

    onMounted(() => {
      getCallLogs();
    });

    return {
      profile,
      branch,
      fullName,
      connected,
      callLogs,
      profileMenu,
      toggleDialPad,
      toggleProfileMenu,
      notifyingCallLog,
      isMobile,
      isTablet,
      callLogToggle,
      callBottomSheetOpen,
      toggleCallLog,
      startCall,
      changePassword,
      logout,
      moment,
      faSearch,
      faBell,
      faPhone,
      faArrowUp,
      CALL_DIRECTIONS,
      CALL_LOG_STATUSES,
      DURATION_FORMAT,
      DEFAULT_PROFILE_PICTURE,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/colors";

.right-header {
  .branch {
    display: inline-block;
    vertical-align: middle;
    font-weight: bold;
    color: $independence;
    cursor: default;
  }

  .split-v {
    width: 1px;
    height: 20px;
    background-color: $alto;
    display: inline-block;
    vertical-align: middle;
    margin: 0 15px;
  }

  .notifications {
    display: inline-block;
    vertical-align: middle;
    margin-left: -12px;
    margin-right: -12px;

    .icon-wrapper {
      display: inline-block;
      vertical-align: middle;
      position: relative;
      margin: 0 15px;

      .indicator {
        width: 12px;
        height: 12px;
        border: 2px solid $babyPowder;
        border-radius: 50%;
        background-color: $yellowCrayola;
        position: absolute;
        bottom: -5px;
        right: -5px;
        display: none;
      }

      > .icon {
        width: 20px;
        height: 20px;
      }

      &.notify {
        .indicator {
          display: block;
        }
      }
    }
  }

  .icon {
    color: $lavenderGrey;
    vertical-align: middle;
    transition: all .2s ease-in-out;
    cursor: pointer;

    &:hover {
      color: $geranium;
    }
  }

  .call-log {
    overflow-y: scroll;
    list-style: none;
    padding: 0;
    margin: -15px;
    border: 1px solid $alto;

    @media (min-width: 992px) {
      width: 300px;
      max-height: 400px;
    }

    @media (max-width: 992px) {
      margin-top: 30px;
      padding-left: 5px;
    }

    li {
      padding: 15px;
      border-bottom: 1px solid $alto;

      .grey {
        color: $lavenderGrey;
      }

      .icon {
        width: 24px;
        height: 24px;
        margin-right: 15px;
        vertical-align: middle;
        transform: rotate(45deg);
        color: $independence;

        &.inbound {
          transform: rotate(135deg);
        }

        &.green {
          color: $shamrockGreen;
        }

        &.red {
          color: $brightMaroon;
        }
      }

      .info {
        width: calc(100% - 24px -15px);
        display: inline-block;
        vertical-align: middle;

        .number {
          h5 {
            font-weight: bold;
            margin: 0;
            cursor: pointer;
            transition: all .2s ease-in-out;
            font-size: 18px;
            display: inline-block;

            &:hover {
              color: $geranium;
            }
          }
        }

        p {
            margin: 0;

            &.grey {
              line-height: 18px;
            }
        }
      }
    }
  }

  .profile {
    display: inline-block;
    vertical-align: middle;
    margin-left: 30px;

    .name {
      display: inline-block;
      vertical-align: middle;
      cursor: pointer;
    }

    .pic {
      border-radius: 50%;
      display: inline-block;
      vertical-align: middle;
      background-color: $lavenderGrey;
      margin-left: 10px;
      position: relative;
      cursor: pointer;

      @media (max-width: 768px) {
        width: 37px;
        height: 37px;
      }

      @media (min-width: 769px) {
        width: 46px;
        height: 46px;
      }

      img {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        object-fit: cover;
      }

      .phone-connectivity {
        width: 12px;
        height: 12px;
        border: 2px solid $babyPowder;
        border-radius: 50%;
        background-color: $geranium;
        position: absolute;
        bottom: 3px;
        right: 0px;

        &.connected {
          background-color: $shamrockGreen;
        }
      }
    }

    .profile-menu {
      margin: -15px;
      width: 200px;
      text-align: center;

      .split {
        height: 1px;
        width: 100%;
        background-color: $alto;
      }

      .controls {
        padding: 15px;

        .connection-status {
          margin: 0;
          line-height: 18px;
          color: $shamrockGreen;

          .bullet {
            display: inline-block;
            width: 7.5px;
            height: 7.5px;
            background-color: $shamrockGreen;
            border-radius: 50%;
            margin-right: 5px;
            transform: translateY(1px);
          }

          span {
            vertical-align: middle;
          }

          &.disconnected {
            color: $geranium;

            .bullet {
              background-color: $geranium;
            }
          }
        }
      }

      ul {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          padding: 12px 30px;
          transition: all .2s ease-in-out;
          cursor: pointer;

          &:hover {
            background-color: $independence;
            color: $babyPowder;
          }
        }
      }
    }
  }
}
</style>
